import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ExternalLink, Clock, CheckCircle2, XCircle, AlertCircle,
  Timer, RefreshCw, Loader2,
} from "lucide-react";
import api from "@/lib/api";

const SLA_TONE = {
  within:     { label: "SLA cumprido",       ring: "ring-emerald-300", chip: "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border-[hsl(var(--success))]/25" },
  late:       { label: "SLA violado",        ring: "ring-amber-300",   chip: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/25" },
  very_late:  { label: "SLA muito violado",  ring: "ring-rose-300",    chip: "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/25" },
  untouched:  { label: "Sem tratamento",     ring: "ring-rose-400",    chip: "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/25" },
};

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function StageNode({ stage, isLast }) {
  const reached = stage.reached;
  return (
    <li className="relative pl-12 pb-8" data-testid={`timeline-stage-${stage.key}`}>
      {!isLast && (
        <span className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${reached ? "bg-foreground" : "bg-muted"}`}></span>
      )}
      <div className={`absolute left-0 top-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${
        reached ? "bg-foreground border-primary text-background" : "bg-card border-border text-muted-foreground/70"
      }`}>
        <span className="text-lg leading-none">{stage.icon}</span>
      </div>
      <div className={`flex flex-col gap-1 ${reached ? "" : "opacity-60"}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-foreground">{stage.label}</span>
          {reached && stage.duration_since_previous_human && (
            <span className="inline-flex items-center gap-1 text-[11px] bg-muted text-foreground px-2 py-0.5 rounded-full border border-border">
              <Timer className="w-3 h-3" /> {stage.duration_since_previous_human}
            </span>
          )}
          {!reached && (
            <span className="inline-flex items-center gap-1 text-[11px] bg-card text-muted-foreground px-2 py-0.5 rounded-full border border-border">
              <Clock className="w-3 h-3" /> Pendente
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {reached && stage.timestamp ? fmtDT(stage.timestamp) : "aguarda"}
          {stage.actor ? <span className="ml-2 text-muted-foreground">· {stage.actor}</span> : null}
        </div>
      </div>
    </li>
  );
}

function EventCard({ ev }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-b-0" data-testid={`timeline-event-${ev.kind}`}>
      <div className="text-xl leading-none pt-0.5">{ev.emoji || "•"}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{ev.label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {fmtDT(ev.timestamp)} {ev.actor ? <span className="ml-2 text-muted-foreground">· {ev.actor}</span> : null}
        </div>
        {ev.meta && ev.meta.reason && (
          <div className="text-xs text-[hsl(var(--warning))] mt-1">Motivo: {ev.meta.reason}</div>
        )}
        {ev.meta && ev.meta.amount != null && (
          <div className="text-xs text-foreground mt-1">
            Valor: {new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(ev.meta.amount || 0)}
            {ev.meta.method ? ` · ${ev.meta.method}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

export default function LeadTimeline() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${id}/performance-timeline`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar timeline.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-24 text-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> A carregar…
      </div>
    );
  }
  if (!data) return null;

  const sla = data.sla || {};
  const tone = SLA_TONE[sla.status] || SLA_TONE.untouched;

  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="lead-timeline-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(`/leads/${id}`)}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          data-testid="timeline-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar ao lead
        </button>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          data-testid="timeline-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Actualizar
        </button>
      </div>

      {/* Hero — always dark (fixed navy gradient) → uses text-white
          instead of the semantic token because `text-primary-foreground`
          is navy in both themes and would be invisible on top. */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-8 text-white mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-semibold">Percurso do lead</div>
        <h1 className="text-3xl font-semibold mt-2" data-testid="timeline-lead-name">{data.lead.name}</h1>
        <div className="text-sm text-white/60 mt-2 flex flex-wrap gap-3">
          <span>Estado: <b className="text-white">{data.lead.status || "—"}</b></span>
          {data.lead.office && <span>Escritório: <b className="text-white">{data.lead.office}</b></span>}
          {data.lead.district && <span>Distrito: <b className="text-white">{data.lead.district}</b></span>}
          {data.lead.source && <span>Origem: <b className="text-white">{data.lead.source}</b></span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className={`bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 ring-2 ring-inset ${tone.ring}`} data-testid="timeline-sla-card">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold">SLA 15min</div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-2xl">{sla.emoji}</span>
              <span className="text-lg font-semibold text-white">{tone.label}</span>
            </div>
            <div className="text-xs text-white/70 mt-1">
              {sla.actual_minutes != null
                ? `1º contacto: ${sla.actual_minutes} min`
                : "Sem primeiro contacto registado"}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold">Duração total</div>
            <div className="text-2xl font-semibold mt-2 text-white">
              {data.total_duration_human || "—"}
            </div>
            <div className="text-xs text-white/70 mt-1">Do lead recebido até ao último estágio conhecido.</div>
          </div>
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold">Estágios cumpridos</div>
            <div className="text-2xl font-semibold mt-2 text-white">
              {data.stages.filter((s) => s.reached).length} <span className="text-white/50 text-lg">/ {data.stages.length}</span>
            </div>
            <div className="text-xs text-white/70 mt-1">Funil de atendimento em 8 fases.</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Timeline (funnel stages) */}
        <div className="lg:col-span-3 bg-card border border-border rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-1">Funil</div>
          <h2 className="text-lg font-semibold text-foreground mb-6">8 estágios do lead</h2>
          <ol data-testid="timeline-stages-list">
            {data.stages.map((s, idx) => (
              <StageNode key={s.key} stage={s} isLast={idx === data.stages.length - 1} />
            ))}
          </ol>
        </div>

        {/* Events lateral */}
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-1">Eventos</div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Marcos adicionais</h2>
          {data.events.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
              Sem eventos adicionais registados.
            </div>
          ) : (
            <div data-testid="timeline-events-list">
              {data.events.map((ev, i) => (
                <EventCard key={`${ev.kind}-${i}`} ev={ev} />
              ))}
            </div>
          )}
          <Link
            to={`/leads/${id}`}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 text-sm text-foreground hover:text-foreground bg-muted border border-border rounded-lg py-2 font-medium"
            data-testid="timeline-open-lead-btn"
          >
            <ExternalLink className="w-4 h-4" /> Abrir ficha do lead
          </Link>
        </div>
      </div>
    </div>
  );
}
